/**
 * The match ball. (§6.7 adjacent — but really: the most important mesh in the game)
 *
 * HISTORY / WHY THIS FILE EXISTS: the only ball mesh ever created lived in
 * placeholder.ts. When the M7 registry retired the placeholder for the six real
 * subsystems (68ef4c1), every subsystem covered its own domain — and none of
 * them owned the ball. The game shipped a live match with an INVISIBLE ball
 * (vfx.ts draws only the >18 m/s trail and the nets). First user playtest
 * caught it in minutes. This subsystem exists so the ball has exactly one
 * unambiguous owner. If you ever retire a registry entry again, grep for what
 * else it was quietly providing first.
 *
 * Look: classic white ball with black pentagon panels, drawn into a small
 * procedural equirect canvas texture (zero assets, loads instantly), lambert
 * shading so it reads under the same lights as everything else, real shadow.
 * Rotation comes straight from the sim's quaternion via setBall — the ball
 * visibly rolls and screws through the air, which sells the Magnus curve the
 * physics already computes.
 */

import {
  CanvasTexture,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  PlaneGeometry,
  SphereGeometry,
  SRGBColorSpace,
  Vector3,
} from 'three';
import { BALL_RADIUS } from '@fg/shared/sim';
import type {
  QualitySettings,
  RenderContext,
  RenderState,
  RenderSubsystem,
} from './context.js';
import { BallHoldPose, createHeldOffsetTracker, type HeldOffsetTracker } from './handAttach.js';

/** Texture height in px; small — the ball is ~30px on screen at broadcast zoom. */
const TEX_H = 128;

/**
 * Fake contact shadow so the ball never visually floats on Low (no shadow maps).
 *
 * ── THE BLACK BORDER (owner playtest: *"ball was having a black border"*) ────
 *
 * This was a hard-edged, uniformly opaque black `CircleGeometry` at 1.4× the
 * ball's radius sitting 11 mm off the turf. Two things made it read as an
 * outline drawn around the ball rather than as a shadow under it:
 *
 *   1. NO FALLOFF. A real contact shadow is darkest at the contact point and
 *      fades outward; a flat disc has a hard rim, and a hard dark rim 4 cm
 *      outside the ball's silhouette is, visually, a border.
 *   2. IT WAS ALWAYS ON. `lighting.ts` keeps `shadowMap.enabled` true and gates
 *      per-light, so every tier above Low ALREADY casts a real shadow from a
 *      ball with `castShadow = true`. The blob stacked on top of it, doubling
 *      the darkness exactly where the two overlap — under the grounded ball.
 *
 * So: a radial-gradient alpha (no rim to see), and `setQuality` drops it to a
 * whisper once real shadows exist. It is not removed outright on those tiers —
 * with a low sun the real shadow rakes away sideways and leaves nothing beneath
 * the ball, which is the float this blob was added to cure.
 */
const BLOB_OPACITY_NO_SHADOWS = 0.30;
const BLOB_OPACITY_REAL_SHADOWS = 0.10;

// ---------------------------------------------------------------------------
// THE BALL IN THE HAND (§4.5, Law 15) — see handAttach.ts for the seam.
// ---------------------------------------------------------------------------

/**
 * HOW FAR THE DRAWN BALL MAY SIT FROM THE SIMULATED ONE, and only while held.
 *
 * This is the one deliberate divergence in the file, so it gets a number and a
 * reason rather than a shrug. `computeSocket` builds `ContactSocket.Hands` in
 * the player's UPRIGHT frame — 1.18 m above his feet, 0.40 m along his facing —
 * because the server has no skeleton and the sim must stay a pure function of
 * (state, inputs). Standing, that lands within a few centimetres of his gloves.
 * MID-DIVE it does not: he is horizontal, the socket is still measured straight
 * up, and the ball floats clear of the outstretched glove by most of an arm.
 * That gap is what this correction spends, so the cap has to be about an arm:
 * 1.2 m covers a full-stretch dive and refuses anything that could only be a
 * bad bone, an unposed LOD tier, or a hold that was mis-derived.
 *
 * Refusal is silent and total — the ball is drawn exactly where the sim put it,
 * which is the behaviour that predates this file's hold handling. Nothing
 * downstream of the sim (camera, HUD, trail, possession, hash, wire) ever sees
 * the correction; it exists only between `state.ball` and `this.mesh.position`.
 */
const HOLD_MAX_CORRECTION_M = 1.2;

/**
 * GATHER, DON'T SNAP. Time constant for the drawn ball settling into the hand.
 *
 * The sim claims a ball instantly: on the tick `resolveSave` succeeds,
 * `ball.px/py/pz` jumps from wherever the shot was to the hand socket. Drawn
 * literally that is a teleport — the second of the three named failure modes.
 * So the correction is carried as an OFFSET from the simulated ball, seeded on
 * the catch frame with the offset that keeps the drawn ball exactly where it
 * already was, and decayed toward the hand. The ball therefore continues along
 * its flight for a few frames and arrives into the glove. ~0.07 s is ~95% closed
 * in a fifth of a second: fast enough to read as hands taking a ball, slow
 * enough that there is a gather to see.
 */
const HOLD_GATHER_TAU_S = 0.07;

/**
 * RELEASE FAST. The same offset decays to zero the moment the hold ends.
 *
 * A released ball is the one thing that must not lag: it leaves at up to 30 m/s
 * and any easing reads as the ball being on a string. This is short enough
 * (~3 frames at 60 Hz) to be a continuous departure from the glove rather than
 * a visible rubber band, and it is what makes the throw/punt release leave the
 * HAND instead of popping out of the sim's release point.
 */
const HOLD_RELEASE_TAU_S = 0.03;

/**
 * Cap on the seeded gather offset. A hold can begin with the drawn ball a long
 * way from the sim's hand point — a restart that teleports the ball onto the
 * touchline spot, a camera cut, a snapshot correction. Easing a two-metre lead
 * in would look like the ball being reeled in on a line, so the seed is clamped
 * and anything longer simply starts its gather from here.
 */
const HOLD_SEED_MAX_M = 0.9;
/** Extent of the shadow quad, in ball radii. The gradient's dark core is well
 *  inside this — the outer third is already fully transparent. */
const BLOB_EXTENT = 1.25;

function paintBallTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = TEX_H * 2;
  canvas.height = TEX_H;
  const g = canvas.getContext('2d')!;

  // Base: warm white with a hint of grey so pure-white bloom doesn't clip it.
  g.fillStyle = '#f4f4f2';
  g.fillRect(0, 0, canvas.width, canvas.height);

  // Black panels: a staggered grid of rounded pentagons approximates the classic
  // 32-panel look well enough at gameplay distance. Equirect distortion at the
  // poles is invisible on a 0.11m sphere.
  g.fillStyle = '#1a1a1c';
  const cols = 6;
  const rows = 3;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cx = ((c + (r % 2 ? 0.5 : 0)) / cols) * canvas.width;
      const cy = ((r + 0.5) / rows) * canvas.height;
      const rad = TEX_H * 0.11;
      g.beginPath();
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const x = cx + Math.cos(a) * rad;
        const y = cy + Math.sin(a) * rad;
        if (i === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
      }
      g.closePath();
      g.fill();
    }
  }

  // Faint seam lines tie the panels together.
  g.strokeStyle = 'rgba(0,0,0,0.18)';
  g.lineWidth = 2;
  for (let r = 1; r < rows; r++) {
    g.beginPath();
    g.moveTo(0, (r / rows) * canvas.height);
    g.lineTo(canvas.width, (r / rows) * canvas.height);
    g.stroke();
  }

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/**
 * Radial falloff for the contact shadow: opaque at the contact point, fully
 * transparent well before the quad's edge, so the quad has no visible rim at
 * any camera angle. The stops are weighted toward the centre — a linear ramp
 * still reads as a disc with a soft edge, which is the same artefact quieter.
 */
function paintBlobTexture(): CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const g = canvas.getContext('2d')!;
  const r = size / 2;
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  grad.addColorStop(0.0, 'rgba(0,0,0,1)');
  grad.addColorStop(0.30, 'rgba(0,0,0,0.72)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0.30)');
  grad.addColorStop(0.78, 'rgba(0,0,0,0.06)');
  grad.addColorStop(1.0, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  return tex;
}

class BallSystem implements RenderSubsystem {
  readonly name = 'ball';

  private mesh!: Mesh;
  private blob!: Mesh;
  private texture!: CanvasTexture;
  private blobTexture!: CanvasTexture;
  /** Peak opacity of the fake shadow — dropped once real shadow maps exist. */
  private blobPeak = BLOB_OPACITY_NO_SHADOWS;

  /**
   * The hold: `drawn − simulated`, zero whenever the ball is loose. That
   * invariant, and the three failure modes it closes, are documented on
   * `createHeldOffsetTracker`.
   */
  private readonly hold: HeldOffsetTracker = createHeldOffsetTracker({
    maxCorrectionM: HOLD_MAX_CORRECTION_M,
    gatherTauS: HOLD_GATHER_TAU_S,
    releaseTauS: HOLD_RELEASE_TAU_S,
    seedMaxM: HOLD_SEED_MAX_M,
  });
  private readonly simPos = new Vector3();
  private readonly handPos = new Vector3();

  init(ctx: RenderContext): void {
    this.texture = paintBallTexture();
    this.blobTexture = paintBlobTexture();
    this.mesh = new Mesh(
      // 24×18 is smooth enough for a 0.11m sphere that never fills the screen.
      new SphereGeometry(BALL_RADIUS, 24, 18),
      new MeshLambertMaterial({ map: this.texture }),
    );
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = false;
    this.mesh.name = 'match-ball';

    const d = BALL_RADIUS * 2 * BLOB_EXTENT;
    this.blob = new Mesh(
      new PlaneGeometry(d, d),
      new MeshBasicMaterial({
        color: 0x000000,
        map: this.blobTexture,
        transparent: true,
        opacity: this.blobPeak,
        depthWrite: false,
      }),
    );
    this.blob.rotation.x = -Math.PI / 2;
    this.blob.name = 'match-ball-shadow-blob';

    ctx.scene.add(this.mesh);
    ctx.scene.add(this.blob);
    this.setQuality(ctx.quality);
  }

  /** Real shadow maps make the fake blob mostly redundant — see BLOB_OPACITY_*. */
  setQuality(quality: QualitySettings): void {
    this.blobPeak =
      quality.shadowMapSize > 0 ? BLOB_OPACITY_REAL_SHADOWS : BLOB_OPACITY_NO_SHADOWS;
  }

  update(state: RenderState, ctx: RenderContext): void {
    const b = state.ball;
    this.simPos.set(b.px, b.py, b.pz);

    const draw = this.resolveDrawPosition(state, ctx);

    this.mesh.position.set(draw.x, Math.max(draw.y, BALL_RADIUS), draw.z);
    this.mesh.quaternion.set(b.qx, b.qy, b.qz, b.qw);

    // Contact blob: sits on the turf, fades and shrinks as the ball rises. It
    // follows the DRAWN ball — a shadow under the simulated one would be a
    // second ball's worth of evidence that the two disagree.
    const h = Math.max(0, draw.y - BALL_RADIUS);
    const fade = Math.max(0, 1 - h / 6);
    this.blob.visible = fade > 0.01;
    if (this.blob.visible) {
      this.blob.position.set(draw.x, 0.011, draw.z); // just above pitch, below lines' z-fight range
      const s = 1 + h * 0.25; // shadow spreads a little with height
      this.blob.scale.set(s, s, s);
      (this.blob.material as MeshBasicMaterial).opacity = this.blobPeak * fade;
    }
  }

  /**
   * Where to draw the ball: the sim's position, plus a bounded correction that
   * exists only while somebody is holding it. `ctx.attachments` is the ONLY
   * channel through which this subsystem learns anything about a skeleton —
   * see `handAttach.ts` for the seam and for what the offset is doing.
   */
  private resolveDrawPosition(state: RenderState, ctx: RenderContext): Vector3 {
    const held = state.ballHeldBy;
    const pose = state.ballHoldPose;
    const anchored =
      held >= 0 &&
      pose !== BallHoldPose.None &&
      ctx.attachments.handAnchor(held, pose, this.simPos, this.handPos);
    return this.hold.step(
      this.simPos, anchored ? this.handPos : null, held, state.dt, this.mesh.position,
    );
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshLambertMaterial).dispose();
    this.texture.dispose();
    this.blob.geometry.dispose();
    (this.blob.material as MeshBasicMaterial).dispose();
    this.blobTexture.dispose();
  }
}

export function createBallSystem(): RenderSubsystem {
  return new BallSystem();
}
